/**
 * BSA obligation register — CLARID-SPEC-BSA-001 §3.2.
 *
 * Two tabs, in this order deliberately:
 *
 *   1. Risk-based check — diagnostic #7. The reason the register exists.
 *   2. Register — the obligations themselves.
 *
 * A calendar of duties is a commodity. The finding that a program *claims* to be
 * risk-based while treating every tier identically is not, and it's what a BSA
 * officer will care about in the first thirty seconds. So it leads.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tab } from '@headlessui/react';
import {
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { bsaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FlatCadenceMatrix, { type FlatCadenceData } from '../components/bsa/FlatCadenceMatrix';

type RiskTier = 'LOW' | 'MODERATE_LOW' | 'MODERATE' | 'MODERATE_HIGH' | 'HIGH';

interface Obligation {
  id: string;
  category: string;
  title: string;
  description: string | null;
  citation: string | null;
  scope: string;
  scopeLabel: string | null;
  frequencyMonths: number;
  frequencySource: 'REGULATION' | 'RISK_BASED' | 'BANK_POLICY';
  riskTier: RiskTier | null;
  lastCompletedAt: string | null;
  nextDueAt: string;
  status: string;
  evidenceRequired: boolean;
  events: { id: string; occurredAt: string; evidenceSha256: string | null }[];
}

const TIER_LABEL: Record<RiskTier, string> = {
  LOW: 'Low',
  MODERATE_LOW: 'Moderate-low',
  MODERATE: 'Moderate',
  MODERATE_HIGH: 'Moderate-high',
  HIGH: 'High',
};

const TIERS: RiskTier[] = ['LOW', 'MODERATE_LOW', 'MODERATE', 'MODERATE_HIGH', 'HIGH'];

const cadence = (m: number) =>
  m === 1 ? 'Monthly' : m === 3 ? 'Quarterly' : m === 6 ? 'Semi-annual' : m === 12 ? 'Annual' : m === 24 ? 'Every 2 years' : m === 36 ? 'Every 3 years' : `Every ${m} months`;

function dueTone(nextDueAt: string): { label: string; cls: string } {
  const days = Math.ceil((new Date(nextDueAt).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, cls: 'text-red-700 font-medium' };
  if (days <= 30) return { label: `Due in ${days}d`, cls: 'text-amber-700' };
  return { label: new Date(nextDueAt).toLocaleDateString(), cls: 'text-gray-600' };
}

export default function BsaObligationsPage() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const [savingId, setSavingId] = useState<string | null>(null);

  const { data: regData, isLoading } = useQuery({
    queryKey: ['bsa-obligations', teamId],
    queryFn: async () => (await bsaApi.obligations.list(teamId!)).data,
    enabled: !!teamId,
  });

  const { data: diagnostic } = useQuery({
    queryKey: ['bsa-flat-cadence', teamId],
    queryFn: async () => (await bsaApi.diagnostics.flatCadence(teamId!)).data as FlatCadenceData,
    enabled: !!teamId,
  });

  const obligations = (regData?.obligations ?? []) as Obligation[];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['bsa-obligations', teamId] });
    queryClient.invalidateQueries({ queryKey: ['bsa-flat-cadence', teamId] });
  };

  const bootstrap = useMutation({
    mutationFn: async () => (await bsaApi.obligations.bootstrap(teamId!)).data,
    onSuccess: invalidate,
  });

  const project = useMutation({
    mutationFn: async () => (await bsaApi.obligations.project(teamId!)).data,
    onSuccess: invalidate,
  });

  const patch = useMutation({
    mutationFn: async (v: { id: string; data: Record<string, unknown> }) =>
      (await bsaApi.obligations.update(teamId!, v.id, v.data)).data,
    onSettled: () => { setSavingId(null); invalidate(); },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">BSA obligation register</h1>
          <p className="mt-1 text-sm text-gray-600">
            Every recurring BSA duty, its authority, its risk tier, and the evidence that it ran.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {obligations.length === 0 && (
            <button
              type="button"
              onClick={() => bootstrap.mutate()}
              disabled={bootstrap.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {bootstrap.isPending ? 'Building…' : 'Build my register'}
            </button>
          )}
          {obligations.length > 0 && (
            <button
              type="button"
              onClick={() => project.mutate()}
              disabled={project.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Create calendar tasks for obligations coming due"
            >
              <CalendarDaysIcon className="h-4 w-4" aria-hidden />
              {project.isPending ? 'Adding…' : 'Add due items to calendar'}
            </button>
          )}
        </div>
      </div>

      {project.isSuccess && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-800">
          {(project.data as { created: number; updated: number }).created} task
          {(project.data as { created: number }).created === 1 ? '' : 's'} created,{' '}
          {(project.data as { updated: number }).updated} updated on your calendar.
        </div>
      )}

      {isLoading && <div className="py-16 text-center text-sm text-gray-500">Loading…</div>}

      {!isLoading && obligations.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <ClipboardDocumentCheckIcon className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
          <h3 className="mt-3 text-sm font-medium text-gray-900">No register yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
            Build a starting register of the recurring BSA duties every program owes, each with its
            FFIEC or CFR authority. You can tailor cadence, risk tier, and owner afterwards.
          </p>
        </div>
      )}

      {obligations.length > 0 && (
        <Tab.Group>
          <Tab.List className="flex gap-1 border-b border-gray-200">
            {['Risk-based check', 'Register'].map((t) => (
              <Tab
                key={t}
                className={({ selected }) =>
                  `px-4 py-2 text-sm font-medium border-b-2 -mb-px focus:outline-none ${
                    selected
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`
                }
              >
                {t}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="pt-5">
            {/* Diagnostic #7 leads — it's the reason the register exists. */}
            <Tab.Panel>
              {diagnostic ? (
                <FlatCadenceMatrix data={diagnostic} />
              ) : (
                <div className="py-10 text-center text-sm text-gray-500">Loading check…</div>
              )}
            </Tab.Panel>

            <Tab.Panel>
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Obligation</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Risk tier</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Cadence</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Last done</th>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Next due</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {obligations.map((o) => {
                        const due = dueTone(o.nextDueAt);
                        const fixed = o.frequencySource === 'REGULATION';
                        return (
                          <tr key={o.id} className={savingId === o.id ? 'opacity-50' : undefined}>
                            <td className="px-4 py-3 align-top">
                              <div className="font-medium text-gray-900">{o.title}</div>
                              {o.scopeLabel && (
                                <div className="text-xs text-gray-500">{o.scopeLabel}</div>
                              )}
                              {o.citation && (
                                <div className="mt-0.5 text-[11px] text-gray-400">{o.citation}</div>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top">
                              {fixed ? (
                                <span className="text-xs text-gray-400">n/a</span>
                              ) : (
                                <select
                                  value={o.riskTier ?? ''}
                                  onChange={(e) => {
                                    setSavingId(o.id);
                                    patch.mutate({
                                      id: o.id,
                                      data: { riskTier: e.target.value || null },
                                    });
                                  }}
                                  className="rounded border-gray-300 text-xs focus:border-gray-500 focus:ring-gray-500"
                                >
                                  <option value="">Not set</option>
                                  {TIERS.map((t) => (
                                    <option key={t} value={t}>{TIER_LABEL[t]}</option>
                                  ))}
                                </select>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top">
                              {fixed ? (
                                /* Set by rule. 314(a) does not become quarterly
                                   because a bank would prefer it. */
                                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                  <LockClosedIcon className="h-3 w-3" aria-hidden />
                                  {cadence(o.frequencyMonths)}
                                  <span className="text-gray-400">· by regulation</span>
                                </span>
                              ) : (
                                <select
                                  value={o.frequencyMonths}
                                  onChange={(e) => {
                                    setSavingId(o.id);
                                    patch.mutate({
                                      id: o.id,
                                      data: { frequencyMonths: Number(e.target.value) },
                                    });
                                  }}
                                  className="rounded border-gray-300 text-xs focus:border-gray-500 focus:ring-gray-500"
                                >
                                  {[1, 3, 6, 12, 18, 24, 36].map((m) => (
                                    <option key={m} value={m}>{cadence(m)}</option>
                                  ))}
                                </select>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top text-gray-600">
                              {o.lastCompletedAt ? (
                                <div>
                                  {new Date(o.lastCompletedAt).toLocaleDateString()}
                                  {o.events[0]?.evidenceSha256 && (
                                    <div
                                      className="text-[11px] text-emerald-700"
                                      title={`SHA-256 ${o.events[0].evidenceSha256}`}
                                    >
                                      evidence hashed
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              )}
                            </td>

                            <td className={`px-4 py-3 align-top ${due.cls}`}>{due.label}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500">
                <ShieldCheckIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                Cadences fixed by regulation can’t be shortened or extended here. Everything else is a
                risk-based default you can tailor — and the risk-based check will tell you whether your
                tailoring actually differentiates.
              </p>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      )}
    </div>
  );
}
